import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiX, FiMessageSquare, FiSearch, FiUser, FiUsers, FiInfo, FiLayers,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { workflowsAPI, usersAPI, departmentsAPI } from '../../services/api';
import SearchableSelect from '../SearchableSelect';
import { localizeSeeded } from '../../pages/FlowManagement/flowUtils';
import './WorkflowActionModals.scss';

// Görüş Al — the action owner asks N users for a non-blocking opinion.
// Targets are picked per person or through a Birim (unit): pick the unit
// first, then any of its members. With more than one person the requester
// also chooses parallel/sequential delivery and the any-one/all completion
// rule. The owner keeps their action throughout.
function OpinionRequestModal({ open, onClose, approval, onDone }) {
  const { t, i18n } = useTranslation();
  const [pickMode, setPickMode] = useState('person'); // 'person' | 'unit'
  const [selected, setSelected] = useState([]);
  // Person search
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  // Unit flow
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [deptUsers, setDeptUsers] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  // Group behaviour
  const [mode, setMode] = useState('parallel');
  const [completionRule, setCompletionRule] = useState('all');
  const [note, setNote] = useState('');
  // Document abilities for the opinion giver(s) — commenting needs no switch,
  // it is always theirs. Both off by default; scoped to this request only.
  const [grantVersions, setGrantVersions] = useState(false);
  const [grantSupporting, setGrantSupporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPickMode('person'); setSelected([]); setQuery(''); setResults([]);
    setDepartmentId(''); setDeptUsers([]); setMode('parallel'); setCompletionRule('all'); setNote('');
    setGrantVersions(false); setGrantSupporting(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    departmentsAPI.list({ isActive: 'true' })
      .then(r => setDepartments((r.data.departments || []).filter(d => d.isActive !== false)))
      .catch(() => {});
  }, [open]);

  // Person search (debounced)
  useEffect(() => {
    if (!open || pickMode !== 'person') return undefined;
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await usersAPI.search(q, 8);
        setResults(r.data.users || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, pickMode]);

  // Unit flow: department picked → list its members
  useEffect(() => {
    if (!open || !departmentId) { setDeptUsers([]); return; }
    setDeptLoading(true);
    workflowsAPI.opinionDepartmentUsers(departmentId)
      .then(r => setDeptUsers(r.data.users || []))
      .catch(() => setDeptUsers([]))
      .finally(() => setDeptLoading(false));
  }, [open, departmentId]);

  const isSelected = (id) => selected.some(u => String(u._id) === String(id));
  const addUser = (u) => { if (!isSelected(u._id)) setSelected(prev => [...prev, u]); };
  const removeUser = (id) => setSelected(prev => prev.filter(u => String(u._id) !== String(id)));
  const toggleUser = (u) => (isSelected(u._id) ? removeUser(u._id) : addUser(u));

  const handleSubmit = async () => {
    if (selected.length === 0) return toast.error(t('workflowActions.opinion.targetsRequired'));
    setSubmitting(true);
    try {
      await workflowsAPI.requestOpinion(approval._id, {
        userIds: selected.map(u => u._id),
        departmentId: pickMode === 'unit' && departmentId ? departmentId : undefined,
        mode: selected.length > 1 ? mode : 'parallel',
        completionRule: selected.length > 1 ? completionRule : 'all',
        note: note.trim() || undefined,
        grants: {
          canUploadVersions: grantVersions,
          canAddSupportingDocs: grantSupporting,
        },
      });
      toast.success(t('workflowActions.opinion.success', { count: selected.length }));
      onClose();
      onDone?.();
    } catch (err) {
      const code = err.response?.data?.code;
      const codeMsg = {
        OPINION_NOT_ALLOWED: t('workflowActions.opinion.errNotAllowed'),
        TARGET_INACTIVE: t('workflowActions.opinion.errInactive'),
        STEP_NOT_ACTIVE: t('workflowActions.errStepNotActive'),
      }[code];
      toast.error(codeMsg || err.response?.data?.error || t('workflowActions.opinion.failed'));
    }
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div className="wfam-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="wfam wfam--wide" onClick={e => e.stopPropagation()}>
        <header className="wfam__header">
          <div className="wfam__title">
            <span className="wfam__title-icon wfam__title-icon--opinion"><FiMessageSquare size={16} /></span>
            <div>
              <h3>{t('workflowActions.opinion.title')}</h3>
              <p>{localizeSeeded(approval?.stepName, i18n.language)}</p>
            </div>
          </div>
          <button className="wfam__close" onClick={onClose} aria-label={t('common.cancel')}><FiX size={16} /></button>
        </header>

        <div className="wfam__body">
          {/* Person / Unit switch */}
          <div className="wfam__seg">
            <button
              type="button"
              className={`wfam__seg-btn ${pickMode === 'person' ? 'is-active' : ''}`}
              onClick={() => setPickMode('person')}
            >
              <FiUser size={13} /> {t('workflowActions.opinion.pickPerson')}
            </button>
            <button
              type="button"
              className={`wfam__seg-btn ${pickMode === 'unit' ? 'is-active' : ''}`}
              onClick={() => setPickMode('unit')}
            >
              <FiLayers size={13} /> {t('workflowActions.opinion.pickUnit')}
            </button>
          </div>

          {pickMode === 'person' ? (
            <div className="wfam__field">
              <label>{t('workflowActions.opinion.personLabel')}</label>
              <div className="wfam__search">
                <FiSearch className="wfam__search-icon" />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('workflowActions.searchUserPlaceholder')}
                  autoFocus
                  // An in-app people search — the browser's contact/address
                  // autofill must stay out of it.
                  name="wfam-user-search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {(results.length > 0 || searching) && (
                  <div className="wfam__results">
                    {searching && <div className="wfam__results-note">{t('common.loading')}</div>}
                    {results.map(u => (
                      <button
                        key={u._id}
                        type="button"
                        className={`wfam__result ${isSelected(u._id) ? 'is-added' : ''}`}
                        onClick={() => { addUser(u); setQuery(''); setResults([]); }}
                      >
                        <span className="wfam__avatar"><FiUser size={12} /></span>
                        <span className="wfam__result-info">
                          <strong>{u.firstName} {u.lastName}</strong>
                          <em>{u.email}</em>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="wfam__field">
                <label>{t('workflowActions.opinion.unitLabel')}</label>
                <SearchableSelect
                  value={departmentId}
                  onChange={setDepartmentId}
                  placeholder={t('workflowActions.opinion.selectUnit')}
                  options={departments.map(d => ({ value: d._id, label: d.name }))}
                />
              </div>
              {departmentId && (
                <div className="wfam__field">
                  <label>{t('workflowActions.opinion.unitMembers')}</label>
                  {deptLoading ? (
                    <div className="wfam__results-note">{t('common.loading')}</div>
                  ) : deptUsers.length === 0 ? (
                    <div className="wfam__results-note">{t('workflowActions.opinion.unitEmpty')}</div>
                  ) : (
                    <div className="wfam__member-list">
                      {deptUsers.map(u => (
                        <label key={u._id} className={`wfam__member ${isSelected(u._id) ? 'is-on' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isSelected(u._id)}
                            onChange={() => toggleUser(u)}
                          />
                          <span className="wfam__avatar">{(u.firstName?.[0] || '') + (u.lastName?.[0] || '')}</span>
                          <span className="wfam__result-info">
                            <strong>{u.firstName} {u.lastName}</strong>
                            <em>{u.email}</em>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Selected chips */}
          {selected.length > 0 && (
            <div className="wfam__field">
              <label>{t('workflowActions.opinion.selectedLabel', { count: selected.length })}</label>
              <div className="wfam__chips">
                {selected.map(u => (
                  <span key={u._id} className="wfam__chip">
                    {u.firstName} {u.lastName}
                    <button type="button" onClick={() => removeUser(u._id)} aria-label={t('common.cancel')}>
                      <FiX size={11} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Document abilities for the opinion giver(s). Commenting is
              always allowed — no switch. Both grants live and die with this
              request; permanent roles are never touched. */}
          {selected.length > 0 && (
            <div className="wfam__field">
              <label>{t('workflowActions.opinion.grantsLabel')}</label>
              <div className="wfam__switches">
                <label className={`wfam__switch ${grantVersions ? 'is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={grantVersions}
                    onChange={(e) => setGrantVersions(e.target.checked)}
                  />
                  <span className="wfam__switch-track" aria-hidden="true"><span className="wfam__switch-knob" /></span>
                  <span className="wfam__switch-text">
                    <strong>{t('workflowActions.opinion.grantVersions')}</strong>
                    <em>{t('workflowActions.opinion.grantVersionsDesc')}</em>
                  </span>
                </label>
                <label className={`wfam__switch ${grantSupporting ? 'is-on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={grantSupporting}
                    onChange={(e) => setGrantSupporting(e.target.checked)}
                  />
                  <span className="wfam__switch-track" aria-hidden="true"><span className="wfam__switch-knob" /></span>
                  <span className="wfam__switch-text">
                    <strong>{t('workflowActions.opinion.grantSupporting')}</strong>
                    <em>{t('workflowActions.opinion.grantSupportingDesc')}</em>
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Group behaviour — only meaningful for 2+ people */}
          {selected.length > 1 && (
            <div className="wfam__rules">
              <div className="wfam__field">
                <label>{t('workflowActions.opinion.modeLabel')}</label>
                <div className="wfam__radio-row">
                  <label className={`wfam__radio ${mode === 'parallel' ? 'is-on' : ''}`}>
                    <input type="radio" name="wfam-mode" checked={mode === 'parallel'} onChange={() => setMode('parallel')} />
                    <span>
                      <strong>{t('workflowActions.opinion.modeParallel')}</strong>
                      <em>{t('workflowActions.opinion.modeParallelDesc')}</em>
                    </span>
                  </label>
                  <label className={`wfam__radio ${mode === 'sequential' ? 'is-on' : ''}`}>
                    <input type="radio" name="wfam-mode" checked={mode === 'sequential'} onChange={() => setMode('sequential')} />
                    <span>
                      <strong>{t('workflowActions.opinion.modeSequential')}</strong>
                      <em>{t('workflowActions.opinion.modeSequentialDesc')}</em>
                    </span>
                  </label>
                </div>
              </div>
              <div className="wfam__field">
                <label>{t('workflowActions.opinion.ruleLabel')}</label>
                <div className="wfam__radio-row">
                  <label className={`wfam__radio ${completionRule === 'any' ? 'is-on' : ''}`}>
                    <input type="radio" name="wfam-rule" checked={completionRule === 'any'} onChange={() => setCompletionRule('any')} />
                    <span>
                      <strong>{t('workflowActions.opinion.ruleAny')}</strong>
                      <em>{t('workflowActions.opinion.ruleAnyDesc')}</em>
                    </span>
                  </label>
                  <label className={`wfam__radio ${completionRule === 'all' ? 'is-on' : ''}`}>
                    <input type="radio" name="wfam-rule" checked={completionRule === 'all'} onChange={() => setCompletionRule('all')} />
                    <span>
                      <strong>{t('workflowActions.opinion.ruleAll')}</strong>
                      <em>{t('workflowActions.opinion.ruleAllDesc')}</em>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="wfam__field">
            <label>{t('workflowActions.opinion.noteLabel')}</label>
            <textarea
              className="wfam__textarea"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={t('workflowActions.opinion.notePlaceholder')}
              maxLength={2000}
            />
          </div>

          <div className="wfam__note">
            <FiInfo size={13} />
            <span>{t('workflowActions.opinion.hint')}</span>
          </div>
        </div>

        <footer className="wfam__footer">
          <button className="wfam__btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="wfam__btn wfam__btn--opinion" onClick={handleSubmit} disabled={submitting || selected.length === 0}>
            <FiUsers size={14} />
            {submitting ? t('workflowActions.submitting') : t('workflowActions.opinion.confirm')}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default OpinionRequestModal;
